/**
 * batchMTService.ts
 * Place at: src/lib/cat/batchMTService.ts
 */

import { mtFetch, MtLimitError, MtSessionError } from "@/lib/mtClient";

export type BatchMTProvider = "deepl" | "google" | "mymemory";

export interface BatchSegment {
    id: string;
    source_text: string;
    source_tagged_text?: string | null;
    target_text: string | null;
    segment_order: number;
}

export interface BatchProgress {
    completed: number;
    total: number;
    percentage: number;
    status: "running" | "complete" | "cancelled" | "error";
    message: string;
    failedSegments: string[];
}

export interface BatchTranslateResult {
    success: boolean;
    translated: Record<string, string>;
    charactersUsed: number;
    segmentsTranslated: number;
    failedSegments: string[];
    provider: BatchMTProvider;
    timeTakenMs: number;
    error?: string;
    // True when the server stopped the batch because the plan limit was reached.
    limitReached?: boolean;
}

const BACKEND_DEEPL = `${(import.meta as any).env?.VITE_API_URL || "https://api.linguaproz.com"}/api/translate/deepl`;
const BACKEND_GOOGLE = `${(import.meta as any).env?.VITE_API_URL || "https://api.linguaproz.com"}/api/translate/google`;

const BATCH_SIZES: Record<BatchMTProvider, number> = {
    deepl: 50,
    google: 100,
    mymemory: 1,
};

const BATCH_DELAYS: Record<BatchMTProvider, number> = {
    deepl: 200,
    google: 100,
    mymemory: 350,
};

const LANG_NAME_TO_CODE: Record<string, string> = {
    english: "en",
    afrikaans: "af",
    portuguese: "pt",
    french: "fr",
    spanish: "es",
    german: "de",
    dutch: "nl",
    italian: "it",
    arabic: "ar",
    chinese: "zh",
    japanese: "ja",
    korean: "ko",
    russian: "ru",
    polish: "pl",
    swedish: "sv",
    norwegian: "no",
    danish: "da",
    finnish: "fi",
    czech: "cs",
    hungarian: "hu",
    romanian: "ro",
    greek: "el",
    turkish: "tr",
    hebrew: "he",
    hindi: "hi",
    thai: "th",
    indonesian: "id",
    malay: "ms",
    swahili: "sw",
    zulu: "zu",
    xhosa: "xh",
    sotho: "st",
    tswana: "tn",
    venda: "ve",
    tsonga: "ts",
    ndebele: "nr",
};

const normaliseCode = (lang: string): string => {
    if (!lang) return lang;
    if (lang.length <= 5 && !lang.includes(" ")) return lang.toLowerCase();
    return LANG_NAME_TO_CODE[lang.toLowerCase()] ?? lang.toLowerCase();
};

// Luso-African Portuguese variants (pt-AO, pt-MZ, pt-CV, pt-GW, pt-ST, pt-TL) are not offered
// by MT engines, so MyMemory gets pt-PT for them. The project keeps its own language code.
const toMyMemoryTarget = (lang: string): string => {
    const code = normaliseCode(lang);
    return code.startsWith("pt-") && code !== "pt-br" ? "pt-PT" : code;
};

function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function translateDeepL(
    texts: string[],
    sourceLang: string,
    targetLang: string,
    signal?: AbortSignal,
): Promise<string[]> {
    const body: Record<string, unknown> = {
        text: texts,
        target_lang: normaliseCode(targetLang).toUpperCase(),
    };
    if (sourceLang && sourceLang !== "auto")
        body.source_lang = normaliseCode(sourceLang)
            .toUpperCase()
            .split("-")[0];
    const res = await mtFetch(BACKEND_DEEPL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(`DeepL ${res.status}: ${err.error || res.statusText}`);
    }
    const data = await res.json();
    return data.translations.map((t: { text: string }) => t.text);
}

async function translateGoogle(
    texts: string[],
    sourceLang: string,
    targetLang: string,
    signal?: AbortSignal,
): Promise<string[]> {
    const body: Record<string, unknown> = {
        text: texts,
        target: normaliseCode(targetLang).split("-")[0],
    };
    if (sourceLang && sourceLang !== "auto")
        body.source = normaliseCode(sourceLang).split("-")[0];
    const res = await mtFetch(BACKEND_GOOGLE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(`Google ${res.status}: ${err.error || res.statusText}`);
    }
    const data = await res.json();
    return data.data.translations.map(
        (t: { translatedText: string }) => t.translatedText,
    );
}

async function translateMyMemory(
    text: string,
    sourceLang: string,
    targetLang: string,
    signal?: AbortSignal,
): Promise<string> {
    const langPair = `${normaliseCode(sourceLang)}|${toMyMemoryTarget(targetLang)}`;
    const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${encodeURIComponent(langPair)}`;
    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error(`MyMemory ${res.status}`);
    const data = await res.json();
    if (data.responseStatus !== 200)
        throw new Error(`MyMemory: ${data.responseDetails}`);
    return data.responseData.translatedText;
}

export async function batchTranslate(params: {
    segments: BatchSegment[];
    provider: BatchMTProvider;
    sourceLang: string;
    targetLang: string;
    emptyOnly: boolean;
    onProgress: (p: BatchProgress) => void;
    signal?: AbortSignal;
}): Promise<BatchTranslateResult> {
    const {
        segments,
        provider,
        sourceLang,
        targetLang,
        emptyOnly,
        onProgress,
        signal,
    } = params;
    const start = Date.now();
    const translated: Record<string, string> = {};
    const failedSegments: string[] = [];
    let charactersUsed = 0;

    const toTranslate = emptyOnly
        ? segments.filter((s) => !s.target_text || s.target_text.trim() === "")
        : segments.filter((s) => s.source_text?.trim());

    if (toTranslate.length === 0) {
        return {
            success: true,
            translated: {},
            charactersUsed: 0,
            segmentsTranslated: 0,
            failedSegments: [],
            provider,
            timeTakenMs: 0,
        };
    }

    const batchSize = BATCH_SIZES[provider];
    const batches: BatchSegment[][] = [];
    for (let i = 0; i < toTranslate.length; i += batchSize) {
        batches.push(toTranslate.slice(i, i + batchSize));
    }

    const total = toTranslate.length;
    let completed = 0;

    for (let bi = 0; bi < batches.length; bi++) {
        if (signal?.aborted) {
            onProgress({
                completed,
                total,
                percentage: Math.round((completed / total) * 100),
                status: "cancelled",
                message: "Cancelled by user",
                failedSegments,
            });
            return {
                success: false,
                translated,
                charactersUsed,
                segmentsTranslated: completed - failedSegments.length,
                failedSegments,
                provider,
                timeTakenMs: Date.now() - start,
                error: "Cancelled",
            };
        }

        const batch = batches[bi];
        onProgress({
            completed,
            total,
            percentage: Math.round((completed / total) * 100),
            status: "running",
            message: `Translating segments ${completed + 1}–${Math.min(completed + batch.length, total)} of ${total}…`,
            failedSegments,
        });

        try {
            if (provider === "mymemory") {
                for (const seg of batch) {
                    if (signal?.aborted) break;
                    try {
                        const result = await translateMyMemory(
                            seg.source_text,
                            sourceLang,
                            targetLang,
                            signal,
                        );
                        translated[seg.id] = result;
                        charactersUsed += seg.source_text.length;
                    } catch {
                        failedSegments.push(seg.id);
                    }
                    completed++;
                    if (!signal?.aborted) await sleep(BATCH_DELAYS.mymemory);
                }
            } else {
                // Use HTML-tagged text when available (preserves formatting through MT)
                const texts = batch.map(
                    (s) => s.source_tagged_text || s.source_text,
                );
                const results =
                    provider === "deepl"
                        ? await translateDeepL(
                              texts,
                              sourceLang,
                              targetLang,
                              signal,
                          )
                        : await translateGoogle(
                              texts,
                              sourceLang,
                              targetLang,
                              signal,
                          );
                results.forEach((text, i) => {
                    const seg = batch[i];
                    // If we sent tagged text, result is tagged — store as tagged
                    if (seg.source_tagged_text) {
                        translated[seg.id] = text; // tagged HTML result
                    } else {
                        translated[seg.id] = text; // plain text result
                    }
                    charactersUsed += seg.source_text.length;
                });
                completed += batch.length;
            }
        } catch (err) {
            batch.forEach((s) => failedSegments.push(s.id));
            completed += batch.length;
            const msg = err instanceof Error ? err.message : String(err);
            if (err instanceof MtLimitError || err instanceof MtSessionError) {
                onProgress({
                    completed,
                    total,
                    percentage: Math.round((completed / total) * 100),
                    status: "error",
                    message: msg,
                    failedSegments,
                });
                return {
                    success: false,
                    translated,
                    charactersUsed,
                    segmentsTranslated: completed - failedSegments.length,
                    failedSegments,
                    provider,
                    timeTakenMs: Date.now() - start,
                    error: msg,
                    limitReached: err instanceof MtLimitError,
                };
            }
            if (
                msg.includes("403") ||
                msg.includes("API key") ||
                msg.includes("401")
            ) {
                onProgress({
                    completed,
                    total,
                    percentage: Math.round((completed / total) * 100),
                    status: "error",
                    message: msg,
                    failedSegments,
                });
                return {
                    success: false,
                    translated,
                    charactersUsed,
                    segmentsTranslated: completed - failedSegments.length,
                    failedSegments,
                    provider,
                    timeTakenMs: Date.now() - start,
                    error: msg,
                };
            }
        }

        if (bi < batches.length - 1 && !signal?.aborted)
            await sleep(BATCH_DELAYS[provider]);
    }

    onProgress({
        completed,
        total,
        percentage: 100,
        status: "complete",
        message: `Translated ${completed - failedSegments.length} segments successfully`,
        failedSegments,
    });
    return {
        success: true,
        translated,
        charactersUsed,
        segmentsTranslated: completed - failedSegments.length,
        failedSegments,
        provider,
        timeTakenMs: Date.now() - start,
    };
}

export function countSegmentsToTranslate(
    segments: BatchSegment[],
    emptyOnly: boolean,
): number {
    if (!emptyOnly) return segments.filter((s) => s.source_text?.trim()).length;
    return segments.filter((s) => !s.target_text || s.target_text.trim() === "")
        .length;
}

export function estimateCharacters(
    segments: BatchSegment[],
    emptyOnly: boolean,
): number {
    const filtered = emptyOnly
        ? segments.filter((s) => !s.target_text || s.target_text.trim() === "")
        : segments;
    return filtered.reduce((sum, s) => sum + (s.source_text?.length || 0), 0);
}

export function estimateSeconds(
    count: number,
    provider: BatchMTProvider,
): number {
    const rates: Record<BatchMTProvider, number> = {
        deepl: 0.3,
        google: 0.2,
        mymemory: 0.5,
    };
    const batchSize = BATCH_SIZES[provider];
    return Math.max(
        1,
        Math.round(Math.ceil(count / batchSize) * rates[provider]),
    );
}
